import { useForm } from 'react-hook-form';
import { ErrorMessage, Input } from '#components/atoms/forms/index.ts';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { SelectItem } from '#components/patterns/forms/select/select-item/SelectItem.tsx';
import { SelectControlled } from '#components/patterns/forms/select/select/SelectControlled.tsx';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import type { CIDRRule } from '#dn-types/config-override.ts';

const DEFAULT_VALUES = { cidr: '', allow: '' as const };

type FormData = {
  cidr: string;
  allow: 'Allow' | 'Deny' | '';
};

type Props = {
  defaultValues?: CIDRRule;
  onSave: (values: CIDRRule) => void;
  cidrValidation: (value: string) => string | true;
};

// Convert allow from boolean to enum that the form needs
function getFormValue(value?: CIDRRule) {
  if (value) {
    return { ...value, allow: value.allow ? 'Allow' : 'Deny' } as const;
  }
}

export function RemoteAllowListForm({ defaultValues, onSave, cidrValidation }: Props) {
  const formContext = useForm<FormData>({ defaultValues: getFormValue(defaultValues) ?? DEFAULT_VALUES });
  const { register, handleSubmit, formState, control } = formContext;
  const { errors } = formState;

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    return handleSubmit((data) => {
      return Promise.resolve(onSave({ ...data, allow: data.allow === 'Allow' }));
    })(e);
  };

  return (
    <form method="post" id="lighthouse.remote_allow_list-form" onSubmit={onSubmit}>
      <Spacer direction="vertical" size="wide">
        <Field label="CIDR IP range" className="width-100">
          <Input
            className="width-100"
            hasError={Boolean(errors.cidr)}
            {...register('cidr', { validate: cidrValidation })}
          />
          {errors.cidr && <ErrorMessage>{errors.cidr.message}</ErrorMessage>}
        </Field>
        <SelectControlled
          className="width-100"
          control={control}
          rules={{ required: 'Should this range be allowed or denied?' }}
          name="allow"
          label="Allow"
          placeholder="Select…"
          errorMessage={errors.allow?.message}
          renderValue={(v) => v || null}
          renderOptions={({ onChange }) => (
            <>
              {['Allow', 'Deny'].map((v) => (
                <SelectItem key={v} value={v} onClick={() => onChange(v)}>
                  {v}
                </SelectItem>
              ))}
            </>
          )}
        />
      </Spacer>
    </form>
  );
}
